import { supabase } from './supabase';
import { logException, breadcrumb } from './logger';
import { withSearchQuery } from './search-navigation';
import type {
  PaginatedResponse,
  ProductRow,
  VariantRow,
  StoreLocation,
  Product,
  Order,
  Customer,
  InventoryLevel,
  SyncEvent,
  AuditLogEntry,
  DashboardStats,
  ConnectionStatus,
  GlobalSearchResult,
} from '@/types';

// ── Helpers ──────────────────────────────────────────────────

function handleError(error: unknown, operation: string): never {
  logException(error, { operation });
  throw error;
}

function buildNoRowsUpdatedError(operation: string): Error {
  return new Error(`${operation} failed because no rows were updated. This usually means the browser session can read the row but does not have permission to write it.`);
}

/**
 * Extract a meaningful error from Supabase functions.invoke responses.
 * Supabase wraps errors in FunctionsHttpError / FunctionsRelayError / FunctionsFetchError.
 */
async function extractFunctionError(
  error: { message: string; context?: { json?: () => Promise<unknown> }; name?: string } | null,
  operation: string,
): Promise<Error> {
  if (!error) return new Error(`${operation}: Unknown error`);

  // Supabase FunctionsHttpError has a context with json() method
  let detail: unknown;
  try {
    if (error.context && typeof error.context.json === 'function') {
      detail = await error.context.json();
    }
  } catch {
    // ignore extraction failure
  }

  const enrichedError = new Error(
    detail && typeof detail === 'object' && detail !== null && 'error' in detail
      ? String((detail as Record<string, unknown>).error)
      : error.message,
  );
  enrichedError.name = error.name ?? 'FunctionError';
  return enrichedError;
}

// ── Products (via product_list_view) ─────────────────────────

export interface ProductsParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  status?: string;
  product_type?: string;
  price_comparison?: string;
}

export async function fetchProducts(
  params: ProductsParams = {},
): Promise<PaginatedResponse<ProductRow>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_products', { search_query: search }, { count: 'exact' })
    : supabase.from('product_list_view').select('*', { count: 'exact' });

  if (params.status && params.status !== 'all') {
    query = query.eq('status', params.status);
  }

  if (params.product_type) {
    query = query.eq('product_type', params.product_type);
  }

  if (params.price_comparison) {
    switch (params.price_comparison) {
      case 'no_nursery':
        query = query.is('at_nursery_price', null);
        break;
      case 'nursery_lower':
        query = query.lt('price_diff', 0);
        break;
      case 'nursery_higher':
        query = query.gt('price_diff', 0);
        break;
      case 'same':
        query = query.eq('price_diff', 0);
        break;
      case 'on_sale':
        query = query.eq('is_on_sale', true);
        break;
    }
  }

  const sortBy = params.sort_by ?? 'title';
  const ascending = (params.sort_dir ?? 'asc') === 'asc';
  if (search) query = query.order('search_rank', { ascending: false });
  query = query.order(sortBy, { ascending, nullsFirst: false });
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchProducts');

  return {
    data: (data ?? []) as ProductRow[],
    total: count ?? 0,
    page,
    per_page: perPage,
  };
}

/**
 * One product with the variants and images the app is allowed to show.
 *
 * Variants Shopify no longer has are soft-deleted, not removed, so they are
 * filtered out here — the list screens get that from `variant_list_view`, and a
 * detail page that skipped it would show variants the store no longer sells.
 * Ordering by position stops rows from reshuffling between refetches.
 */
export async function fetchProduct(shopifyProductId: string | number) {
  const { data: product, error: pErr } = await supabase
    .from('products')
    .select('*, variants(*), product_images(*)')
    .eq('shopify_product_id', Number(shopifyProductId))
    .eq('variants.is_deleted', false)
    .order('position', { referencedTable: 'variants', ascending: true, nullsFirst: false })
    .order('position', { referencedTable: 'product_images', ascending: true, nullsFirst: false })
    .single();

  if (pErr) handleError(pErr, 'fetchProduct');

  // Inventory levels are keyed by Shopify's inventory *item* id, not variant id.
  const inventoryItemIds = (product.variants ?? [])
    .map((v: { shopify_inventory_item_id: number | null }) => v.shopify_inventory_item_id)
    .filter((id: number | null): id is number => id != null);

  let inventoryLevels: InventoryLevel[] = [];
  if (inventoryItemIds.length > 0) {
    const { data, error } = await supabase
      .from('inventory_list_view')
      .select('*')
      .in('shopify_inventory_item_id', inventoryItemIds);
    if (error) handleError(error, 'fetchProduct');
    inventoryLevels = (data ?? []) as InventoryLevel[];
  }

  return { product: product as Product, inventory_levels: inventoryLevels };
}

/**
 * Push the queued `local_edits` for several products to Shopify in parallel
 * (small chunks). `push-to-shopify` clears each product's edits on success.
 * Collects every product's failure and throws one combined error so the caller
 * can surface it — no edit is ever silently dropped.
 */
export async function pushProductsToShopify(shopifyProductIds: number[]): Promise<void> {
  const ids = [...new Set(shopifyProductIds)];
  if (ids.length === 0) return;
  breadcrumb('Pushing products to Shopify', { count: ids.length });
  const errors: string[] = [];
  const CHUNK = 4;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const results = await Promise.allSettled(chunk.map((id) => pushToShopify(id)));
    results.forEach((r, idx) => {
      if (r.status === 'rejected') {
        errors.push(`Product ${chunk[idx]}: ${(r.reason as Error).message}`);
      }
    });
  }
  if (errors.length > 0) {
    handleError(new Error(errors.join('; ')), 'pushProductsToShopify');
  }
}

/**
 * Write product field edits (title, description, status, tags, type, vendor,
 * handle) and push them straight to Shopify. The edit is recorded in
 * `local_edits` — merged onto anything already pending — so `push-to-shopify`
 * knows what to send; on success it clears the queue. Throws if the push fails.
 */
export async function updateProductFields(
  shopifyProductId: number,
  fields: Record<string, unknown>,
): Promise<void> {
  breadcrumb('Editing product', { shopifyProductId, fields: Object.keys(fields) });
  const { data: current, error: readErr } = await supabase
    .from('products')
    .select('local_edits')
    .eq('shopify_product_id', shopifyProductId)
    .single();
  if (readErr) handleError(readErr, 'updateProductFields');

  const merged = {
    ...((current?.local_edits as Record<string, unknown> | null) ?? {}),
    ...fields,
  };
  const { data, error } = await supabase
    .from('products')
    .update({ ...fields, local_edits: merged, has_local_edits: true })
    .eq('shopify_product_id', shopifyProductId)
    .select('id');

  if (error) handleError(error, 'updateProductFields');
  if (!data?.length) handleError(buildNoRowsUpdatedError('updateProductFields'), 'updateProductFields');

  await pushToShopify(shopifyProductId);
}

/**
 * Current tag lists for a set of products — used by the bulk tag editor to
 * compute a per-product result for "add" / "remove" (which must merge against
 * each product's existing tags rather than overwrite).
 */
export async function fetchProductTagsByIds(
  shopifyProductIds: number[],
): Promise<{ shopify_product_id: number; tags: string[] | null }[]> {
  if (shopifyProductIds.length === 0) return [];
  const { data, error } = await supabase
    .from('products')
    .select('shopify_product_id, tags')
    .in('shopify_product_id', shopifyProductIds);
  if (error) handleError(error, 'fetchProductTagsByIds');
  return (data ?? []) as { shopify_product_id: number; tags: string[] | null }[];
}

/** One product's pending field edits in a bulk operation. */
export interface ProductFieldUpdate {
  shopifyProductId: number;
  fields: Record<string, unknown>;
}

/**
 * Apply field edits to many products at once and push every one to Shopify —
 * each product may receive different field values (e.g. its own merged tag
 * list). Writes locally in small concurrent batches, then pushes. Throws if any
 * push fails (the combined error names every product that failed).
 */
export async function bulkUpdateProductFields(
  updates: ProductFieldUpdate[],
): Promise<{ updated: number }> {
  if (updates.length === 0) return { updated: 0 };
  const ids = [...new Set(updates.map((u) => u.shopifyProductId))];
  breadcrumb('Bulk editing products', { count: updates.length });

  const { data: existing, error: readErr } = await supabase
    .from('products')
    .select('shopify_product_id, local_edits')
    .in('shopify_product_id', ids);
  if (readErr) handleError(readErr, 'bulkUpdateProductFields');
  const editsById = new Map<number, Record<string, unknown>>(
    (existing ?? []).map((r) => [
      r.shopify_product_id as number,
      (r.local_edits as Record<string, unknown> | null) ?? {},
    ]),
  );

  let updated = 0;
  const CHUNK = 10;
  for (let i = 0; i < updates.length; i += CHUNK) {
    const chunk = updates.slice(i, i + CHUNK);
    const results = await Promise.all(
      chunk.map((u) =>
        supabase
          .from('products')
          .update({
            ...u.fields,
            local_edits: { ...(editsById.get(u.shopifyProductId) ?? {}), ...u.fields },
            has_local_edits: true,
          })
          .eq('shopify_product_id', u.shopifyProductId)
          .select('id'),
      ),
    );
    for (const { data, error } of results) {
      if (error) handleError(error, 'bulkUpdateProductFields');
      updated += data?.length ?? 0;
    }
  }
  if (updated === 0) {
    handleError(buildNoRowsUpdatedError('bulkUpdateProductFields'), 'bulkUpdateProductFields');
  }

  await pushProductsToShopify(ids);
  return { updated };
}

/** One variant's nursery fields. Omit a field to leave it untouched in Shopify. */
export interface NurseryFieldUpdate {
  shopify_variant_id: number;
  nursery_price?: number | null;
  nursery_only?: boolean;
}

/**
 * Push nursery fields to Shopify via the `push-nursery-price` Edge Function:
 * the "At Nursery" price (`custom_fields.offline_price`) and/or the
 * nursery-only flag (`custom_fields.nursery`). Those metafields are the
 * canonical store the storefront and legacy plugin read, so an edit is not
 * truly "saved" until this has run. Throws if the Edge Function fails OR if it
 * reports per-variant errors (it returns those with a 200 status).
 */
export async function pushNurseryFieldsToShopify(
  variants: NurseryFieldUpdate[],
): Promise<void> {
  if (variants.length === 0) return;
  breadcrumb('Pushing nursery fields to Shopify', { count: variants.length });
  const { data, error } = await supabase.functions.invoke('push-nursery-price', {
    body: { variants },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'pushNurseryFieldsToShopify');
    handleError(enriched, 'pushNurseryFieldsToShopify');
  }

  const errs = (data as { errors?: string[] } | null)?.errors;
  if (errs && errs.length > 0) {
    handleError(new Error(errs.join('; ')), 'pushNurseryFieldsToShopify');
  }
}

/**
 * Set the nursery price on a single variant — mirrors the Shopify
 * `custom_fields.offline_price` metafield. Writes locally, then pushes to
 * Shopify; if the push fails this throws and the row keeps `has_local_edits`.
 */
export async function updateNurseryPrice(
  shopifyVariantId: number,
  price: number | null,
): Promise<void> {
  breadcrumb('Updating nursery price', { shopifyVariantId, price });
  const { data, error } = await supabase
    .from('variants')
    .update({ nursery_price: price, has_local_edits: true })
    .eq('shopify_variant_id', shopifyVariantId)
    .select('id');

  if (error) handleError(error, 'updateNurseryPrice');
  if (!data?.length) handleError(buildNoRowsUpdatedError('updateNurseryPrice'), 'updateNurseryPrice');

  await pushNurseryFieldsToShopify([
    { shopify_variant_id: shopifyVariantId, nursery_price: price },
  ]);
}

/**
 * Set the "nursery only" flag on a single variant — mirrors the Shopify
 * `custom_fields.nursery` metafield (1 = sold only at the nursery, not online).
 * Writes locally, then pushes to Shopify.
 */
export async function updateNurseryOnly(
  shopifyVariantId: number,
  nurseryOnly: boolean,
): Promise<void> {
  breadcrumb('Updating nursery-only flag', { shopifyVariantId, nurseryOnly });
  const { data, error } = await supabase
    .from('variants')
    .update({ nursery_only: nurseryOnly, has_local_edits: true })
    .eq('shopify_variant_id', shopifyVariantId)
    .select('id');

  if (error) handleError(error, 'updateNurseryOnly');
  if (!data?.length) handleError(buildNoRowsUpdatedError('updateNurseryOnly'), 'updateNurseryOnly');

  await pushNurseryFieldsToShopify([
    { shopify_variant_id: shopifyVariantId, nursery_only: nurseryOnly },
  ]);
}

/**
 * Set nursery prices on many variants at once, where each variant may get a
 * different price (e.g. "match online price" or "online + 10%"). Writes locally
 * in small concurrent batches, then pushes every price to Shopify in one call.
 * Returns how many rows were updated. Throws if the Shopify push fails.
 */
export async function bulkSetNurseryPrices(
  updates: { shopifyVariantId: number; price: number | null }[],
): Promise<{ updated: number }> {
  breadcrumb('Bulk setting nursery prices', { count: updates.length });
  let updated = 0;
  const CHUNK = 10;
  for (let i = 0; i < updates.length; i += CHUNK) {
    const chunk = updates.slice(i, i + CHUNK);
    const results = await Promise.all(
      chunk.map(({ shopifyVariantId, price }) =>
        supabase
          .from('variants')
          .update({ nursery_price: price, has_local_edits: true })
          .eq('shopify_variant_id', shopifyVariantId)
          .select('id'),
      ),
    );
    for (const { data, error } of results) {
      if (error) handleError(error, 'bulkSetNurseryPrices');
      updated += data?.length ?? 0;
    }
  }
  if (updates.length > 0 && updated === 0) {
    handleError(buildNoRowsUpdatedError('bulkSetNurseryPrices'), 'bulkSetNurseryPrices');
  }

  await pushNurseryFieldsToShopify(
    updates.map((u) => ({ shopify_variant_id: u.shopifyVariantId, nursery_price: u.price })),
  );
  return { updated };
}

// ── Variants (via variant_list_view) ─────────────────────────

export interface VariantsParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** Filters on the parent product's status. */
  status?: string;
  product_type?: string;
  price_comparison?: string;
}

/** Paginated list of variants — one row per variant, with product context. */
export async function fetchVariants(
  params: VariantsParams = {},
): Promise<PaginatedResponse<VariantRow>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_variants', { search_query: search }, { count: 'exact' })
    : supabase.from('variant_list_view').select('*', { count: 'exact' });

  if (params.status && params.status !== 'all') {
    query = query.eq('product_status', params.status);
  }

  if (params.product_type) {
    query = query.eq('product_type', params.product_type);
  }

  if (params.price_comparison) {
    switch (params.price_comparison) {
      case 'no_nursery':
        query = query.is('nursery_price', null);
        break;
      case 'nursery_lower':
        query = query.lt('price_diff', 0);
        break;
      case 'nursery_higher':
        query = query.gt('price_diff', 0);
        break;
      case 'same':
        query = query.eq('price_diff', 0);
        break;
      case 'on_sale':
        query = query.eq('is_on_sale', true);
        break;
    }
  }

  const sortBy = params.sort_by ?? 'product_title';
  const ascending = (params.sort_dir ?? 'asc') === 'asc';
  if (search) query = query.order('search_rank', { ascending: false });
  query = query.order(sortBy, { ascending, nullsFirst: false });
  // Stable secondary sort so variants of one product stay grouped + ordered.
  if (sortBy !== 'product_title') query = query.order('product_title', { ascending: true });
  query = query.order('position', { ascending: true, nullsFirst: false });
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchVariants');

  return {
    data: (data ?? []) as VariantRow[],
    total: count ?? 0,
    page,
    per_page: perPage,
  };
}

/**
 * Every (non-deleted) variant of the given products, ordered by position —
 * used to populate the expandable variant rows on the Products list.
 */
export async function fetchVariantsByProductIds(
  shopifyProductIds: number[],
): Promise<VariantRow[]> {
  if (shopifyProductIds.length === 0) return [];
  const { data, error } = await supabase
    .from('variant_list_view')
    .select('*')
    .in('shopify_product_id', shopifyProductIds)
    .order('product_title', { ascending: true })
    .order('position', { ascending: true, nullsFirst: false });
  if (error) handleError(error, 'fetchVariantsByProductIds');
  return (data ?? []) as VariantRow[];
}

/**
 * Edit fields on a single variant (price, SKU, barcode, options, taxable) and
 * push to Shopify. Mirrors {@link updateProductFields}: the change is queued in
 * `local_edits`, then `push-to-shopify` sends it and clears the queue.
 */
export async function updateVariantFields(
  shopifyVariantId: number,
  shopifyProductId: number,
  fields: Record<string, unknown>,
): Promise<void> {
  breadcrumb('Editing variant', { shopifyVariantId, fields: Object.keys(fields) });
  const { data: current, error: readErr } = await supabase
    .from('variants')
    .select('local_edits')
    .eq('shopify_variant_id', shopifyVariantId)
    .single();
  if (readErr) handleError(readErr, 'updateVariantFields');

  const merged = {
    ...((current?.local_edits as Record<string, unknown> | null) ?? {}),
    ...fields,
  };
  const { data, error } = await supabase
    .from('variants')
    .update({ ...fields, local_edits: merged, has_local_edits: true })
    .eq('shopify_variant_id', shopifyVariantId)
    .select('id');

  if (error) handleError(error, 'updateVariantFields');
  if (!data?.length) handleError(buildNoRowsUpdatedError('updateVariantFields'), 'updateVariantFields');

  await pushToShopify(shopifyProductId);
}

/**
 * Replace a product's complete variant order and push it to Shopify.
 *
 * Position is queued in each variant's local edits so a failed Shopify request
 * remains retryable through the normal product push flow.
 */
export async function reorderProductVariants(
  shopifyProductId: number,
  shopifyVariantIds: number[],
): Promise<void> {
  if (shopifyVariantIds.length < 2) return;

  const uniqueIds = new Set(shopifyVariantIds);
  if (uniqueIds.size !== shopifyVariantIds.length) {
    handleError(new Error('Variant order contains duplicate variants.'), 'reorderProductVariants');
  }

  breadcrumb('Reordering variants', {
    shopifyProductId,
    count: shopifyVariantIds.length,
  });

  const { data: existing, error: readErr } = await supabase
    .from('variants')
    .select('shopify_variant_id, local_edits')
    .eq('shopify_product_id', shopifyProductId)
    .eq('is_deleted', false);
  if (readErr) handleError(readErr, 'reorderProductVariants');

  const rows = existing ?? [];
  const existingIds = new Set(rows.map((row) => Number(row.shopify_variant_id)));
  if (
    rows.length !== shopifyVariantIds.length ||
    shopifyVariantIds.some((id) => !existingIds.has(id))
  ) {
    handleError(
      new Error('Variant list changed. Refresh the product before reordering.'),
      'reorderProductVariants',
    );
  }

  const editsById = new Map<number, Record<string, unknown>>(
    rows.map((row) => [
      Number(row.shopify_variant_id),
      (row.local_edits as Record<string, unknown> | null) ?? {},
    ]),
  );

  const results = await Promise.all(
    shopifyVariantIds.map((shopifyVariantId, index) => {
      const position = index + 1;
      return supabase
        .from('variants')
        .update({
          position,
          local_edits: { ...editsById.get(shopifyVariantId), position },
          has_local_edits: true,
        })
        .eq('shopify_product_id', shopifyProductId)
        .eq('shopify_variant_id', shopifyVariantId)
        .select('id');
    }),
  );

  for (const { data, error } of results) {
    if (error) handleError(error, 'reorderProductVariants');
    if (!data?.length) {
      handleError(buildNoRowsUpdatedError('reorderProductVariants'), 'reorderProductVariants');
    }
  }

  await pushToShopify(shopifyProductId);
}

/** One variant's pending field edits in a bulk operation. */
export interface VariantFieldUpdate {
  shopifyVariantId: number;
  shopifyProductId: number;
  fields: Record<string, unknown>;
}

/**
 * Apply field edits to many variants at once, then push every parent product
 * to Shopify — each variant may receive different values (e.g. an online price
 * derived per-row, or a transformed SKU). Throws if any push fails.
 */
export async function bulkUpdateVariantFields(
  updates: VariantFieldUpdate[],
): Promise<{ updated: number }> {
  if (updates.length === 0) return { updated: 0 };
  breadcrumb('Bulk editing variants', { count: updates.length });
  const ids = updates.map((u) => u.shopifyVariantId);

  const { data: existing, error: readErr } = await supabase
    .from('variants')
    .select('shopify_variant_id, local_edits')
    .in('shopify_variant_id', ids);
  if (readErr) handleError(readErr, 'bulkUpdateVariantFields');
  const editsById = new Map<number, Record<string, unknown>>(
    (existing ?? []).map((r) => [
      r.shopify_variant_id as number,
      (r.local_edits as Record<string, unknown> | null) ?? {},
    ]),
  );

  let updated = 0;
  const CHUNK = 10;
  for (let i = 0; i < updates.length; i += CHUNK) {
    const chunk = updates.slice(i, i + CHUNK);
    const results = await Promise.all(
      chunk.map((u) =>
        supabase
          .from('variants')
          .update({
            ...u.fields,
            local_edits: { ...(editsById.get(u.shopifyVariantId) ?? {}), ...u.fields },
            has_local_edits: true,
          })
          .eq('shopify_variant_id', u.shopifyVariantId)
          .select('id'),
      ),
    );
    for (const { data, error } of results) {
      if (error) handleError(error, 'bulkUpdateVariantFields');
      updated += data?.length ?? 0;
    }
  }
  if (updated === 0) {
    handleError(buildNoRowsUpdatedError('bulkUpdateVariantFields'), 'bulkUpdateVariantFields');
  }

  await pushProductsToShopify(updates.map((u) => u.shopifyProductId));
  return { updated };
}

/**
 * Active store locations, for scoping inventory edits. The primary location is
 * ordered first so it can be the sensible default in pickers.
 */
export async function fetchLocations(): Promise<StoreLocation[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('shopify_location_id, name')
    .eq('active', true)
    .order('is_primary', { ascending: false })
    .order('name', { nullsFirst: false });
  if (error) handleError(error, 'fetchLocations');
  return (data ?? []) as StoreLocation[];
}

/**
 * Set the same stock quantity on many variants at a given location, then push
 * every level to Shopify (Shopify owns stock). Also refreshes the cached
 * per-variant quantity so the list does not look stale before the next sync.
 * Throws if no level rows match (the variants are not stocked there) or the
 * Shopify push fails.
 */
export async function bulkSetInventory(
  items: { shopify_inventory_item_id: number; shopify_variant_id: number }[],
  locationId: number,
  available: number,
): Promise<{ updated: number }> {
  if (items.length === 0) return { updated: 0 };
  breadcrumb('Bulk setting inventory', { count: items.length, locationId, available });

  let updated = 0;
  const CHUNK = 10;
  for (let i = 0; i < items.length; i += CHUNK) {
    const chunk = items.slice(i, i + CHUNK);
    const results = await Promise.all(
      chunk.map((it) =>
        supabase
          .from('inventory_levels')
          .update({ available })
          .eq('shopify_inventory_item_id', it.shopify_inventory_item_id)
          .eq('shopify_location_id', locationId)
          .select('id'),
      ),
    );
    for (const { data, error } of results) {
      if (error) handleError(error, 'bulkSetInventory');
      updated += data?.length ?? 0;
    }
  }
  if (updated === 0) {
    handleError(
      new Error(
        'No inventory levels were updated — the selected variants are not stocked at this location.',
      ),
      'bulkSetInventory',
    );
  }

  // Keep the cached per-variant quantity in step with the level just written.
  for (let i = 0; i < items.length; i += CHUNK) {
    await Promise.all(
      items.slice(i, i + CHUNK).map((it) =>
        supabase
          .from('variants')
          .update({ inventory_quantity: available })
          .eq('shopify_variant_id', it.shopify_variant_id),
      ),
    );
  }

  await pushInventoryToShopify(
    items.map((it) => ({
      shopify_inventory_item_id: it.shopify_inventory_item_id,
      shopify_location_id: locationId,
      available,
    })),
  );
  return { updated };
}

// ── Orders ───────────────────────────────────────────────────

export interface OrdersParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  financial_status?: string;
  fulfillment_status?: string;
}

export async function fetchOrders(
  params: OrdersParams = {},
): Promise<PaginatedResponse<Order>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_orders', { search_query: search }, { count: 'exact' })
    : supabase.from('orders').select('*', { count: 'exact' });

  if (params.financial_status && params.financial_status !== 'all') {
    query = query.eq('financial_status', params.financial_status);
  }

  if (params.fulfillment_status && params.fulfillment_status !== 'all') {
    // Shopify leaves fulfillment_status NULL for unfulfilled orders.
    if (params.fulfillment_status === 'unfulfilled') {
      query = query.is('fulfillment_status', null);
    } else {
      query = query.eq('fulfillment_status', params.fulfillment_status);
    }
  }

  if (search) query = query.order('search_rank', { ascending: false });
  query = query.order(params.sort_by ?? 'shopify_created_at', {
    ascending: (params.sort_dir ?? 'desc') === 'asc',
    nullsFirst: false,
  });
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchOrders');

  return { data: (data ?? []) as Order[], total: count ?? 0, page, per_page: perPage };
}

export async function fetchOrder(shopifyOrderId: string | number): Promise<Order> {
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_line_items(*), refunds(*)')
    .eq('shopify_order_id', Number(shopifyOrderId))
    .single();

  if (error) handleError(error, 'fetchOrder');
  return data as Order;
}

// ── Customers ────────────────────────────────────────────────

export interface CustomersParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export async function fetchCustomers(
  params: CustomersParams = {},
): Promise<PaginatedResponse<Customer>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_customers', { search_query: search }, { count: 'exact' })
    : supabase.from('customers').select('*', { count: 'exact' });

  if (search) query = query.order('search_rank', { ascending: false });
  const sortBy = params.sort_by === 'name' ? 'last_name' : (params.sort_by ?? 'last_name');
  query = query.order(sortBy, {
    ascending: (params.sort_dir ?? 'asc') === 'asc',
    nullsFirst: false,
  });
  if (sortBy === 'last_name') {
    query = query.order('first_name', {
      ascending: (params.sort_dir ?? 'asc') === 'asc',
      nullsFirst: false,
    });
  }
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchCustomers');

  return { data: (data ?? []) as Customer[], total: count ?? 0, page, per_page: perPage };
}

/**
 * Fetch a single customer along with their order history. There is no
 * customer-detail view in the database, so this joins on the client.
 */
export async function fetchCustomer(
  shopifyCustomerId: string | number,
): Promise<{ customer: Customer; orders: Order[] }> {
  const { data: customer, error } = await supabase
    .from('customers')
    .select('*')
    .eq('shopify_customer_id', Number(shopifyCustomerId))
    .single();

  if (error) handleError(error, 'fetchCustomer');

  const { data: orders, error: ordersError } = await supabase
    .from('orders')
    .select('*')
    .eq('customer_id', Number(shopifyCustomerId))
    .order('shopify_created_at', { ascending: false, nullsFirst: false });

  if (ordersError) handleError(ordersError, 'fetchCustomer');

  return { customer: customer as Customer, orders: (orders ?? []) as Order[] };
}

// ── Inventory ────────────────────────────────────────────────

export interface InventoryParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  location_id?: string;
}

export async function fetchInventory(
  params: InventoryParams = {},
): Promise<PaginatedResponse<InventoryLevel>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_inventory', { search_query: search }, { count: 'exact' })
    : supabase.from('inventory_list_view').select('*', { count: 'exact' });

  if (params.location_id) {
    query = query.eq('shopify_location_id', params.location_id);
  }

  if (search) query = query.order('search_rank', { ascending: false });
  query = query.order(params.sort_by ?? 'product_title', {
    ascending: (params.sort_dir ?? 'asc') === 'asc',
    nullsFirst: false,
  });
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchInventory');

  return { data: (data ?? []) as InventoryLevel[], total: count ?? 0, page, per_page: perPage };
}

/**
 * Push inventory levels to Shopify via the `push-inventory` Edge Function.
 * Shopify is the source of truth for stock, so an inventory edit is not truly
 * "saved" until this has run. Throws if the Edge Function fails OR if it
 * reports per-level errors (returned with a 200 status).
 */
export async function pushInventoryToShopify(
  levels: {
    shopify_inventory_item_id: number;
    shopify_location_id: number;
    available: number;
  }[],
): Promise<void> {
  if (levels.length === 0) return;
  breadcrumb('Pushing inventory to Shopify', { count: levels.length });
  const { data, error } = await supabase.functions.invoke('push-inventory', {
    body: { levels },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'pushInventoryToShopify');
    handleError(enriched, 'pushInventoryToShopify');
  }

  const errs = (data as { errors?: string[] } | null)?.errors;
  if (errs && errs.length > 0) {
    handleError(new Error(errs.join('; ')), 'pushInventoryToShopify');
  }
}

/**
 * Set an inventory level. Writes locally first, then pushes the new quantity
 * straight to Shopify (Shopify owns stock). If the push fails this throws so
 * the caller surfaces the error.
 */
export async function updateInventory(
  inventoryItemId: number,
  updates: { available: number; location_id: number },
): Promise<void> {
  breadcrumb('Updating inventory', { inventoryItemId, ...updates });
  const { data, error } = await supabase
    .from('inventory_levels')
    .update({ available: updates.available })
    .eq('shopify_inventory_item_id', inventoryItemId)
    .eq('shopify_location_id', updates.location_id)
    .select('id');

  if (error) handleError(error, 'updateInventory');
  if (!data?.length) handleError(buildNoRowsUpdatedError('updateInventory'), 'updateInventory');

  await pushInventoryToShopify([
    {
      shopify_inventory_item_id: inventoryItemId,
      shopify_location_id: updates.location_id,
      available: updates.available,
    },
  ]);
}

// ── Sync (via Edge Function) ─────────────────────────────────

export async function triggerSync(): Promise<{ success: boolean }> {
  console.log('[Sync] Triggering manual sync...');
  breadcrumb('Triggering Shopify sync');
  const { data, error } = await supabase.functions.invoke('sync', {
    body: { source: 'manual' },
  });

  if (error) {
    console.error('[Sync] Edge function error:', error.message);
    const enriched = await extractFunctionError(error as never, 'triggerSync');
    handleError(enriched, 'triggerSync');
  }
  console.log('[Sync] Sync started successfully:', data);
  return { success: true };
}

export async function pushToShopify(productId: number): Promise<{ success: boolean }> {
  breadcrumb('Pushing to Shopify', { productId });
  const { error } = await supabase.functions.invoke('push-to-shopify', {
    body: { shopify_product_id: productId },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'pushToShopify');
    handleError(enriched, 'pushToShopify');
  }
  return { success: true };
}

export interface SyncLogsParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  category?: string;
  status?: string;
  direction?: string;
}

export async function fetchSyncLogs(
  params: SyncLogsParams = {},
): Promise<PaginatedResponse<SyncEvent>> {
  const page = params.page ?? 1;
  const perPage = params.per_page ?? 50;
  const offset = (page - 1) * perPage;

  const search = params.search?.trim();
  let query = search
    ? supabase.rpc('search_sync_events', { search_query: search }, { count: 'exact' })
    : supabase.from('sync_events').select('*', { count: 'exact' });

  if (params.category && params.category !== 'all') {
    query = query.eq('event_category', params.category);
  }
  if (params.status && params.status !== 'all') {
    query = query.eq('status', params.status);
  }
  if (params.direction && params.direction !== 'all') {
    query = query.eq('direction', params.direction);
  }

  if (search) query = query.order('search_rank', { ascending: false });
  query = query.order(params.sort_by ?? 'created_at', {
    ascending: (params.sort_dir ?? 'desc') === 'asc',
    nullsFirst: false,
  });
  query = query.range(offset, offset + perPage - 1);

  const { data, count, error } = await query;
  if (error) handleError(error, 'fetchSyncLogs');

  return { data: (data ?? []) as SyncEvent[], total: count ?? 0, page, per_page: perPage };
}

// ── Unified Command-K search ────────────────────────────────

interface GlobalSearchRpcRow {
  entity_type: GlobalSearchResult['type'];
  entity_id: string;
  name: string;
  secondary: string | null;
  url: string;
  search_rank: number;
}

export async function searchAllEntities(
  query: string,
  limit = 30,
): Promise<GlobalSearchResult[]> {
  const search = query.trim();
  if (!search) return [];

  const { data, error } = await supabase.rpc('search_all_entities', {
    search_query: search,
    result_limit: limit,
  });
  if (error) handleError(error, 'searchAllEntities');

  return ((data ?? []) as GlobalSearchRpcRow[]).map((row) => {
    const type = row.entity_type;
    const listOnlyEntity = type === 'inventory' || type === 'sync_event';
    return {
      id: `${type}:${row.entity_id}`,
      entityId: row.entity_id,
      name: row.name,
      score: row.search_rank,
      secondary: row.secondary || undefined,
      type,
      url: listOnlyEntity ? withSearchQuery(row.url, search) : row.url,
    };
  });
}

// ── Dashboard ────────────────────────────────────────────────

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data, error } = await supabase
    .from('dashboard_stats')
    .select('*')
    .eq('id', 'current')
    .single();

  if (error) handleError(error, 'fetchDashboardStats');
  return data as DashboardStats;
}

// ── Settings / Connection ────────────────────────────────────

export interface ConnectionCheck {
  valid: boolean;
  shop_name?: string;
  shop_domain?: string | null;
  plan?: string | null;
  error?: string;
}

/**
 * Live check that the app can actually reach Shopify — invokes the
 * `check-connection` Edge Function, which calls Shopify's `shop.json` with the
 * current access token. Used by the Settings page on every visit.
 */
export async function checkConnection(): Promise<ConnectionCheck> {
  breadcrumb('Checking Shopify connection');
  const { data, error } = await supabase.functions.invoke('check-connection');

  if (error) {
    const enriched = await extractFunctionError(error as never, 'checkConnection');
    handleError(enriched, 'checkConnection');
  }

  return data as ConnectionCheck;
}

export async function fetchConnection(): Promise<ConnectionStatus | null> {
  const { data, error } = await supabase
    .from('shopify_connections')
    .select('is_active, shop_domain, shop_name, last_full_sync_at, sync_progress')
    .eq('is_active', true)
    .maybeSingle();

  if (error) handleError(error, 'fetchConnection');

  if (!data) {
    return null;
  }

  return data as ConnectionStatus;
}

export async function clearSyncProgress(): Promise<void> {
  breadcrumb('Clearing stuck sync progress');
  const { data, error } = await supabase
    .from('shopify_connections')
    .update({ sync_progress: null })
    .eq('is_active', true)
    .select('id');

  if (error) handleError(error, 'clearSyncProgress');
  if (!data?.length) {
    handleError(buildNoRowsUpdatedError('clearSyncProgress'), 'clearSyncProgress');
  }
}

export async function fetchProductTypes(): Promise<{ product_type: string; count: number }[]> {
  const { data, error } = await supabase.rpc('get_product_types');
  if (error) handleError(error, 'fetchProductTypes');
  return data ?? [];
}

// ── Audit log / activity ─────────────────────────────────────

export interface AuditLogParams {
  product_id?: number;
  order_id?: number;
  customer_id?: number;
  limit?: number;
}

/**
 * Fetch the activity timeline for one entity. Exactly one of product_id /
 * order_id / customer_id should be supplied — the audit_log table denormalises
 * those keys so an event surfaces on every entity it touches.
 */
export async function fetchAuditLog(
  params: AuditLogParams,
): Promise<AuditLogEntry[]> {
  let query = supabase.from('audit_log').select('*');

  if (params.product_id != null) query = query.eq('product_id', params.product_id);
  if (params.order_id != null) query = query.eq('order_id', params.order_id);
  if (params.customer_id != null) query = query.eq('customer_id', params.customer_id);

  query = query
    .order('created_at', { ascending: false })
    .limit(params.limit ?? 50);

  const { data, error } = await query;
  if (error) handleError(error, 'fetchAuditLog');
  return (data ?? []) as AuditLogEntry[];
}

// ── User management (via admin-users Edge Function) ──────────

export interface AppUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string | null;
  /** Maintained by Supabase Auth — the real last sign-in. */
  last_sign_in_at: string | null;
  /** Newest audit_log entry attributed to this user, or null. */
  last_activity_at: string | null;
}

export interface CreateUserInput {
  email: string;
  password: string;
  full_name?: string;
  role?: string;
}

/**
 * List every app login with role, last sign-in, and last activity. Goes through
 * the `admin-users` Edge Function because the Supabase Auth Admin API (and the
 * service role key it needs) can only run server-side.
 */
export async function listUsers(): Promise<AppUser[]> {
  breadcrumb('Listing app users');
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: { action: 'list' },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'listUsers');
    handleError(enriched, 'listUsers');
  }

  return ((data as { users?: AppUser[] } | null)?.users ?? []);
}

/** Create a new login. The admin sets the password, so the account is active immediately. */
export async function createUser(input: CreateUserInput): Promise<void> {
  breadcrumb('Creating app user', { email: input.email });
  const { error } = await supabase.functions.invoke('admin-users', {
    body: { action: 'create', ...input },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'createUser');
    handleError(enriched, 'createUser');
  }
}

/** Manually override a user's password (admin reset). */
export async function setUserPassword(userId: string, password: string): Promise<void> {
  breadcrumb('Resetting app user password', { userId });
  const { error } = await supabase.functions.invoke('admin-users', {
    body: { action: 'set_password', user_id: userId, password },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'setUserPassword');
    handleError(enriched, 'setUserPassword');
  }
}

/** Change a user's role (viewer / editor / admin). */
export async function updateUserRole(userId: string, role: string): Promise<void> {
  breadcrumb('Updating app user role', { userId, role });
  const { error } = await supabase.functions.invoke('admin-users', {
    body: { action: 'update_role', user_id: userId, role },
  });

  if (error) {
    const enriched = await extractFunctionError(error as never, 'updateUserRole');
    handleError(enriched, 'updateUserRole');
  }
}

// ── Tags ─────────────────────────────────────────────────────

/**
 * Every distinct tag used across the catalog, sorted alphabetically. Powers the
 * tag editor's "pick from existing tags" list. Pages through `products.tags` so
 * the result is complete even past PostgREST's 1000-row cap.
 */
export async function fetchAllTags(): Promise<string[]> {
  const seen = new Set<string>();
  const pageSize = 1000;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('products')
      .select('tags')
      .range(from, from + pageSize - 1);
    if (error) handleError(error, 'fetchAllTags');

    const rows = (data ?? []) as { tags: string[] | null }[];
    for (const row of rows) {
      for (const tag of row.tags ?? []) {
        const trimmed = tag.trim();
        if (trimmed) seen.add(trimmed);
      }
    }
    if (rows.length < pageSize) break;
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}
